import { ChevronsRight } from 'lucide-react';
import Logo from '../brand/Logo';
import Tooltip from '../ui/Tooltip';

/**
 * RailToggle — the COLLAPSED rail's expand control (Figma: Nav / Sidebar header).
 *
 * The brand mark sits in a --nav-header-height (40) header and morphs into a
 * chevron on hover/focus; clicking it expands the sidebar.
 *
 * COLLAPSED-ONLY, BY CONSTRUCTION. This used to render in both states and took a
 * `collapsed` prop to pick its direction and wording. Hovering the logo is a
 * hidden affordance, and once the expanded header grew an explicit close button
 * there was no reason to keep a second, invisible one: Sidebar now renders
 * `<Logo variant="lockup">` when expanded and mounts this only in the rail.
 * The prop is gone rather than defaulted, so the component cannot be put back
 * into a state it no longer has faces for — there is no expanded branch to rot.
 *
 * Geometry: the mark box is --nav-logo-size (32). With the menu container's 8px
 * padding plus this header's 4px inline-start padding the mark lands at (12, 12)
 * — identical to where the expanded lockup's mark lands, exactly as the Figma
 * frame specifies. In the 56px rail that 12 + 32 + 12 also happens to centre it.
 *
 * The tooltip is the portal primitive, not NavItem's CSS one: the sidebar clips
 * overflow, so an absolutely-positioned tooltip could never paint outside it.
 *
 * @param {Function} onToggle  called on click — asks AppShell to expand the rail
 */
export default function RailToggle({ onToggle, className = '' }) {
    // State-accurate wording: this control only ever expands.
    const action = 'Open sidebar';

    return (
        <Tooltip label={action} placement="right">
            <button
                type="button"
                className={`rail-toggle ${className}`.trim()}
                onClick={onToggle}
                aria-label={action}
                aria-expanded={false}
            >
                {/* Both faces are always mounted and cross-faded — an instant
                    swap would be the "instant appear/disappear" anti-pattern. */}
                <span className="rail-toggle__face rail-toggle__face--mark" aria-hidden="true">
                    <Logo variant="mark" size="lg" />
                </span>
                <span className="rail-toggle__face rail-toggle__face--chevron" aria-hidden="true">
                    <ChevronsRight />
                </span>
            </button>
        </Tooltip>
    );
}
